<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import Modal from '$lib/components/ui/Modal.svelte';
  import { workLogsApi } from '$lib/api/workLogs';
  import { workZonesApi } from '$lib/api/workZones';
  import { getErrorMessage } from '$lib/api/client';
  import { reverseGeocode, FALLBACK_NAME, FALLBACK_AREA } from '$lib/utils/mapsLink';
  import { session } from '$lib/state/session.svelte';
  import { t } from '$lib/i18n';
  import type { PunchMode } from '$lib/types';

  function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 1000;
  }

  const { onClose, onSuccess }: { onClose: () => void; onSuccess: () => void } = $props();

  const mode: PunchMode = $derived(session.me?.punch_mode ?? 'site');

  type GeoStatus = 'locating' | 'ok' | 'denied' | 'error';
  let geoStatus = $state<GeoStatus>('locating');
  let geoError = $state('');
  let lat = $state<number | null>(null);
  let lng = $state<number | null>(null);
  let accuracy = $state<number | null>(null);
  let placeName = $state('');
  let placeArea = $state('');
  let looking = $state(false);
  let nameEdited = $state(false);
  let areaEdited = $state(false);
  let geocodeRequestID = 0;

  function requestLocation() {
    // Invalidate the old lookup before its callback can refill stale details.
    geocodeRequestID++;
    looking = false;
    if (!nameEdited) placeName = '';
    if (!areaEdited) placeArea = '';
    lat = null;
    lng = null;
    accuracy = null;
    geoStatus = 'locating';
    geoError = '';
    if (!navigator.geolocation) {
      geoStatus = 'error';
      geoError = t('punchIn.geoUnsupported');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
        accuracy = pos.coords.accuracy;
        geoStatus = 'ok';
      },
      (err) => {
        geoStatus = err.code === err.PERMISSION_DENIED ? 'denied' : 'error';
        geoError =
          err.code === err.PERMISSION_DENIED
            ? t('punchIn.geoDenied')
            : t('punchIn.geoError', { msg: err.message });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }
  let askedGeo = false;
  $effect(() => {
    if (askedGeo) return;
    askedGeo = true;
    requestLocation();
  });

  const zonesQuery = createQuery(() => ({
    queryKey: ['my-work-zones'],
    queryFn: () => workZonesApi.mine(),
    enabled: mode === 'field'
  }));

  const atSite = $derived.by(() => {
    if (lat === null || lng === null) return false;
    for (const z of zonesQuery.data ?? []) {
      if (haversineMeters(lat, lng, z.latitude, z.longitude) <= z.radius) return true;
    }
    return false;
  });

  $effect(() => {
    if (mode !== 'field' || geoStatus !== 'ok' || lat === null || lng === null || atSite) return;
    let cancelled = false;
    const requestID = ++geocodeRequestID;
    looking = true;
    reverseGeocode(lat, lng).then((geo) => {
      if (cancelled || requestID !== geocodeRequestID) return;
      looking = false;
      if (!nameEdited && geo.name && geo.name !== FALLBACK_NAME) placeName = geo.name;
      if (!areaEdited && geo.area && geo.area !== FALLBACK_AREA) placeArea = geo.area;
    });
    return () => {
      cancelled = true;
    };
  });

  let submitting = $state(false);
  let error = $state('');

  const canSubmit = $derived.by(() => {
    if (submitting) return false;
    // Place details and reverse geocoding are optional; GPS is not.
    return geoStatus === 'ok' && lat !== null && lng !== null;
  });

  async function submit() {
    submitting = true;
    error = '';
    try {
      if (mode === 'untagged') {
        await workLogsApi.punchOut({ lat: lat!, lng: lng!, accuracy: accuracy ?? undefined });
      } else if (mode === 'field' && !atSite) {
        await workLogsApi.punchOut({
          lat: lat!,
          lng: lng!,
          accuracy: accuracy ?? undefined,
          remote: true,
          location_name: placeName.trim(),
          location_area: placeArea.trim() || FALLBACK_AREA
        });
      } else {
        await workLogsApi.punchOut({
          lat: lat!,
          lng: lng!,
          accuracy: accuracy ?? undefined
        });
      }
      onSuccess();
      onClose();
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      submitting = false;
    }
  }
</script>

<Modal title={t('punchOut.title')} {onClose}>
  <div class="space-y-5">
    {#if mode === 'home'}
      <div class="rounded-lg border border-toyama-teal/40 bg-toyama-teal/10 p-3.5">
        <p class="text-[13.5px] font-semibold text-navy">{t('punchOut.home.title')}</p>
      </div>
    {/if}
    {#if mode === 'untagged'}
      <p class="text-[13px] leading-relaxed text-dark-gray">{t('punchOut.untagged')}</p>
    {/if}
    {#if true}
      <div>
        <div class="label">{t('common.location')}</div>
        {#if geoStatus === 'locating'}
          <p class="text-[13px] text-dark-gray">{t('punchIn.gettingLocation')}</p>
        {:else if geoStatus === 'ok'}
          <p class="text-[13px] text-toyama-teal">
            {t('punchIn.locationCaptured', { lat: lat?.toFixed(5) ?? '', lng: lng?.toFixed(5) ?? '' })}
          </p>
        {:else}
          <div class="tym-error-state flex items-center justify-between gap-3">
            <span>{geoError}</span>
            <button type="button" class="btn-secondary shrink-0" onclick={requestLocation}>{t('common.retry')}</button>
          </div>
        {/if}
      </div>

      {#if mode === 'field' && geoStatus === 'ok' && !atSite}
        <div>
          <div class="label">{t('punchIn.placeDetails')}</div>
          {#if looking}
            <p class="mb-1.5 text-[12px] text-dark-gray">{t('punchIn.lookingUp')}</p>
          {/if}
          <input type="text" class="input" aria-label={t('punchIn.placeName')} placeholder={t('punchIn.placeName')} bind:value={placeName} oninput={() => (nameEdited = true)} />
          <input type="text" class="input mt-2" aria-label={t('punchIn.area')} placeholder={t('punchIn.area')} bind:value={placeArea} oninput={() => (areaEdited = true)} />
          <p class="mt-1.5 text-[12px] text-dark-gray">{t('punchIn.nameHint')}</p>
        </div>
      {/if}
    {/if}

    {#if error}
      <div class="tym-error-state">{error}</div>
    {/if}
  </div>
  {#snippet footer()}
    <div class="flex justify-end gap-2">
      <button type="button" class="btn-secondary" onclick={onClose}>{t('common.cancel')}</button>
      <button type="button" class="btn-primary" disabled={!canSubmit} onclick={submit}>
        {submitting ? t('punchOut.punchingOut') : t('punchOut.title')}
      </button>
    </div>
  {/snippet}
</Modal>
